/**
 * API Composable
 *
 * Vue composable wrapper around the centralized API client.
 * Provides reactive loading/error state and Vue-specific integrations.
 *
 * =============================================================================
 * THREAT MODEL — TOKEN STORAGE (transitional)
 * =============================================================================
 * Goal: minimize XSS exfiltration of session credentials.
 *
 * - Access token is held ONLY in module-level memory (not in any web storage).
 *   It dies on tab close / hard reload, which is fine: we use the refresh
 *   token to mint a new one on app boot.
 * - Refresh token SHOULD be stored as an httpOnly + Secure + SameSite=Strict
 *   cookie issued by the API. Until that API change ships, we use
 *   sessionStorage as a partial mitigation:
 *     - sessionStorage is per-tab and cleared on tab close (smaller window
 *       than localStorage), and also is NOT shared with subdomains.
 *     - sessionStorage is STILL readable by JS, so XSS can still steal it —
 *       this is why the httpOnly cookie path is mandatory for production.
 *   The cookie write attempt below is a no-op fallback: without `Secure` +
 *   `HttpOnly` it is no stronger than sessionStorage, but documents intent.
 * - User profile (id, email, role) stays in localStorage (not sensitive
 *   enough to warrant the round-trip; needed for fast initial render).
 *
 * TODO(api): switch refresh-token to httpOnly + Secure + SameSite=Strict
 * cookie issued by the API. When that lands, remove sessionStorage fallback
 * and read refresh-token presence via a same-origin /auth/session endpoint
 * instead of reading the cookie value (httpOnly cookies are not readable
 * by JS, by design).
 * =============================================================================
 */

import { ref, shallowRef } from 'vue'
import {
  createApiClient,
  type ApiClient,
  type TokenPair,
  validateApiData,
  ValidationError,
} from '@gencay-akademi/api-client'
import { env } from '@/config/env'

// =============================================================================
// Constants
// =============================================================================

const REFRESH_TOKEN_KEY = 'auth_refresh_token'
const USER_KEY = 'auth_user'

// =============================================================================
// In-memory access token (NEVER persist this)
// =============================================================================

// Module-level state. Access tokens are short-lived and never written to
// any web storage. On hard reload we use the refresh token to mint a new one.
let accessToken: string | null = null

// =============================================================================
// Singleton API Client
// =============================================================================

let apiClientInstance: ApiClient | null = null

function getApiClient(): ApiClient {
  if (!apiClientInstance) {
    apiClientInstance = createApiClient({
      baseUrl: env.VITE_API_URL,
      getToken: () => accessToken,
      getRefreshToken: () => getStoredRefreshToken(),
      onTokenRefresh: (tokens: TokenPair) => {
        // Update in-memory access token only.
        accessToken = tokens.accessToken
        // Refresh-token rotation: store the new refresh token using the
        // best-available transport (httpOnly cookie when API supports it,
        // sessionStorage otherwise — see threat model above).
        saveRefreshToken(tokens.refreshToken)
      },
      onUnauthorized: () => {
        clearAuth()
        const currentPath = window.location.pathname
        const redirectQuery =
          currentPath !== '/login' ? `?redirect=${encodeURIComponent(currentPath)}` : ''
        window.location.href = `/login${redirectQuery}`
      },
    })
  }
  return apiClientInstance
}

// =============================================================================
// Auth Helpers
// =============================================================================

/**
 * Set the in-memory access token. Call this on login + token refresh.
 */
export function setAccessToken(token: string | null): void {
  accessToken = token
}

/**
 * Read the in-memory access token. Use only inside auth/login flow code —
 * normal callers go through the API client which resolves it via getToken.
 */
export function getAccessToken(): string | null {
  return accessToken
}

/**
 * Persist the refresh token using the best transport currently available.
 *
 * Preferred: httpOnly + Secure + SameSite=Strict cookie issued by the API.
 * Until that ships, fall back to sessionStorage (per-tab, cleared on close).
 *
 * NOTE: the `document.cookie` write below is intentionally NOT secure — it
 * is a no-op fallback that documents the intent for the future API change.
 * Without `HttpOnly` (which JS cannot set) it has no XSS-mitigation benefit
 * over sessionStorage; sessionStorage remains the actual store today.
 */
export function saveRefreshToken(refreshToken: string): void {
  // Best-effort cookie set (visible to JS — no httpOnly possible from
  // browser; the API must issue a Set-Cookie response header for the real
  // protection. This line is a forward-compat hint, not a security control).
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : ''
    document.cookie = `${REFRESH_TOKEN_KEY}=${encodeURIComponent(refreshToken)}; Path=/; SameSite=Strict${secure}`
  } catch {
    // ignore — cookie may be blocked in some environments
  }
  // Always also write to sessionStorage as the working fallback until the
  // API switches to issuing httpOnly cookies.
  sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
}

/**
 * Read the refresh token from the best-available transport.
 * sessionStorage today; will be replaced by an API session-probe call once
 * the httpOnly cookie path lands.
 */
export function getStoredRefreshToken(): string | null {
  return sessionStorage.getItem(REFRESH_TOKEN_KEY)
}

/**
 * Save full token pair: access in memory, refresh in storage.
 */
export function saveTokens(tokens: TokenPair): void {
  accessToken = tokens.accessToken
  saveRefreshToken(tokens.refreshToken)
}

/**
 * Get the stored refresh token only (access lives in memory).
 *
 * Returned shape mirrors the original TokenPair for callers that still
 * expect it; access/expiresIn/tokenType are best-effort placeholders.
 */
export function getStoredTokens(): TokenPair | null {
  const refreshToken = getStoredRefreshToken()
  if (!refreshToken) return null
  return {
    accessToken: accessToken ?? '',
    refreshToken,
    expiresIn: 0,
    tokenType: 'Bearer',
  }
}

/**
 * Clear all auth data: in-memory access token, sessionStorage refresh token,
 * cookie fallback, and the localStorage user profile.
 */
export function clearAuth(): void {
  accessToken = null
  sessionStorage.removeItem(REFRESH_TOKEN_KEY)
  // Best-effort cookie clear
  try {
    document.cookie = `${REFRESH_TOKEN_KEY}=; Path=/; Max-Age=0; SameSite=Strict`
  } catch {
    // ignore
  }
  localStorage.removeItem(USER_KEY)
  // Belt-and-braces: remove any legacy localStorage entries from earlier
  // versions so customers upgrading the template clean up automatically.
  localStorage.removeItem('auth_tokens')
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}

// =============================================================================
// Vue Composable
// =============================================================================

/**
 * API composable with reactive state
 */
export function useApi() {
  const loading = ref(false)
  const error = shallowRef<Error | null>(null)
  const client = getApiClient()

  /**
   * Wrap an API call with loading/error state management
   */
  async function withState<T>(apiCall: () => Promise<T>): Promise<T> {
    loading.value = true
    error.value = null

    try {
      const result = await apiCall()
      return result
    } catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err))
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Wrap an API call with validation
   */
  async function withValidation<T>(
    apiCall: () => Promise<T>,
    schema: import('zod').ZodType<T>, // Zod schema
    options?: {
      path?: string[]
      errorMessage?: string
    }
  ): Promise<T> {
    const result = await withState(apiCall)

    try {
      return validateApiData(schema, result, options)
    } catch (validationError) {
      if (validationError instanceof ValidationError) {
        // Re-throw with more context
        throw new Error(`API validation failed: ${validationError.message}`)
      }
      throw validationError
    }
  }

  return {
    // State
    loading,
    error,

    // Client instance for direct access
    client,

    // Helper for state management
    withState,
    withValidation,

    // Re-export auth helpers
    clearAuth,
    saveTokens,
    saveRefreshToken,
    getStoredTokens,
    getStoredRefreshToken,
    setAccessToken,
    getAccessToken,
  }
}

// =============================================================================
// Direct Client Access
// =============================================================================

/**
 * Get the API client instance directly (for use outside Vue components)
 */
export { getApiClient }

// =============================================================================
// Re-exports
// =============================================================================

export type { ApiClient, TokenPair } from '@gencay-akademi/api-client'
export { validateApiData, validateApiResponse, ValidationError } from '@gencay-akademi/api-client'
